import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import MDSpinner from "react-md-spinner";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { fetchFarmWeatherLog } from "../FieldApp/fieldAppApi";

import "./FarmClimateLog.css";

const PERIOD_OPTIONS = [
  { id: "today", label: "Hoje" },
  { id: "week", label: "Última semana" },
];

function periodBounds(periodId) {
  const now = new Date();
  const end = now.toISOString();
  if (periodId === "today") {
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return { dateFrom: start.toISOString(), dateTo: end };
  }
  const start = new Date(now);
  start.setDate(start.getDate() - 7);
  return { dateFrom: start.toISOString(), dateTo: end };
}

function formatRowTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatChartTick(value, periodId) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  if (periodId === "today") {
    return date.toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
    });
  }
  return date.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
  });
}

function metricStats(rows, key) {
  const values = rows
    .map((row) => Number(row[key]))
    .filter((value) => Number.isFinite(value));
  if (!values.length) {
    return { min: null, max: null, avg: null };
  }
  const sum = values.reduce((total, value) => total + value, 0);
  return {
    min: Math.min.apply(null, values),
    max: Math.max.apply(null, values),
    avg: sum / values.length,
  };
}

function formatStat(value, suffix) {
  if (value == null || !Number.isFinite(value)) return "—";
  const rounded = Math.abs(value) >= 10 ? Math.round(value) : Math.round(value * 10) / 10;
  return `${rounded}${suffix}`;
}

const CARDINAL_DEG = {
  N: 0,
  NE: 45,
  L: 90,
  E: 90,
  SE: 135,
  S: 180,
  SO: 225,
  SW: 225,
  O: 270,
  W: 270,
  NO: 315,
  NW: 315,
};

function windFlowDeg(row) {
  const deg = Number(row && row.windDeg);
  if (Number.isFinite(deg)) return (deg + 180) % 360;
  const cardinal = String((row && row.windCardinal) || "")
    .trim()
    .toUpperCase();
  if (!Object.prototype.hasOwnProperty.call(CARDINAL_DEG, cardinal)) {
    return null;
  }
  return (CARDINAL_DEG[cardinal] + 180) % 360;
}

function WindDirectionArrow({ row }) {
  const flowDeg = windFlowDeg(row);
  if (flowDeg == null) return null;
  return (
    <svg
      className="farm-climate-log__wind-arrow"
      viewBox="0 0 64 64"
      style={{ transform: `rotate(${flowDeg}deg)` }}
      aria-hidden="true"
    >
      <path d="M32 6 L44 38 L32 32 L20 38 Z" fill="currentColor" />
      <rect
        x="29.2"
        y="30"
        width="5.6"
        height="26"
        rx="1.2"
        fill="currentColor"
      />
    </svg>
  );
}

function FarmClimateChart({ title, data, dataKey, color, unit, periodId }) {
  return (
    <div className="farm-climate-log__chart">
      <h3>{title}</h3>
      <ResponsiveContainer width="100%" height={240}>
        <LineChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e6e9ed" />
          <XAxis
            dataKey="time"
            tickFormatter={(value) => formatChartTick(value, periodId)}
            minTickGap={28}
            tick={{ fontSize: 11, fill: "#73879c" }}
          />
          <YAxis tick={{ fontSize: 11, fill: "#73879c" }} width={42} />
          <Tooltip
            labelFormatter={(value) => formatRowTime(value)}
            formatter={(value) => [
              value == null ? "—" : `${value}${unit}`,
              title,
            ]}
          />
          <Line
            type="monotone"
            dataKey={dataKey}
            stroke={color}
            strokeWidth={2}
            dot={data.length < 12 ? { r: 3, strokeWidth: 1 } : false}
            name={title}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function FarmClimateLog({ farmId }) {
  const { t } = useTranslation();
  const [period, setPeriod] = useState("today");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const bounds = periodBounds(period);
    fetchFarmWeatherLog(farmId, bounds)
      .then((items) => {
        if (!cancelled) setRows(Array.isArray(items) ? items : []);
      })
      .catch(() => {
        if (!cancelled) setRows([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [farmId, period]);

  const chartData = useMemo(
    () =>
      [...rows]
        .filter((row) => row && row.time)
        .sort((left, right) => new Date(left.time) - new Date(right.time))
        .map((row) => ({
          time: row.time,
          temperatureC:
            row.temperatureC == null ? null : Number(row.temperatureC),
          humidity: row.humidity == null ? null : Number(row.humidity),
          windSpeedKmh:
            row.windSpeedKmh == null ? null : Number(row.windSpeedKmh),
          windCardinal: row.windCardinal || "",
        })),
    [rows]
  );

  const tempStats = metricStats(chartData, "temperatureC");
  const humidityStats = metricStats(chartData, "humidity");
  const windStats = metricStats(chartData, "windSpeedKmh");

  return (
    <section className="farm-climate-log">
      <div className="farm-climate-log__intro">
        <div>
          <h2 className="farm-climate-log__title">{t("Clima da fazenda")}</h2>
          <p className="farm-climate-log__hint">
            {t(
              "Temperatura, vento e umidade gravados a cada 10 minutos no centro da fazenda, mesmo sem máquina no campo."
            )}
          </p>
        </div>
        <div className="farm-climate-log__periods">
          {PERIOD_OPTIONS.map((option) => (
            <button
              key={option.id}
              type="button"
              className={
                period === option.id
                  ? "btn btn-primary btn-sm"
                  : "btn btn-default btn-sm"
              }
              onClick={() => setPeriod(option.id)}
            >
              {t(option.label)}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="farm-climate-log__loading">
          <MDSpinner singleColor="#1ABB9C" size={36} />
        </div>
      ) : null}

      {!loading && !chartData.length ? (
        <p className="farm-climate-log__empty">
          {t(
            "Ainda não há registro de clima da fazenda. A API grava sozinha a cada 10 minutos."
          )}
        </p>
      ) : null}

      {!loading && chartData.length ? (
        <>
          <div className="farm-climate-log__summary">
            <div>
              <span>{t("Temperatura")}</span>
              <strong>
                {formatStat(tempStats.min, "°")} / {formatStat(tempStats.max, "°")}
              </strong>
              <small>
                {t("Mínima")} / {t("Máxima")} · {t("Média")}{" "}
                {formatStat(tempStats.avg, "°")}
              </small>
            </div>
            <div>
              <span>{t("Umidade do ar")}</span>
              <strong>
                {formatStat(humidityStats.min, "%")} /{" "}
                {formatStat(humidityStats.max, "%")}
              </strong>
              <small>
                {t("Mínima")} / {t("Máxima")} · {t("Média")}{" "}
                {formatStat(humidityStats.avg, "%")}
              </small>
            </div>
            <div>
              <span>{t("Vento")}</span>
              <strong>
                {formatStat(windStats.min, " km/h")} /{" "}
                {formatStat(windStats.max, " km/h")}
              </strong>
              <small>
                {t("Mínima")} / {t("Máxima")} · {t("Média")}{" "}
                {formatStat(windStats.avg, " km/h")}
              </small>
            </div>
          </div>

          <div className="farm-climate-log__charts">
            <FarmClimateChart
              title={t("Temperatura")}
              data={chartData}
              dataKey="temperatureC"
              color="#e74c3c"
              unit="°"
              periodId={period}
            />
            <FarmClimateChart
              title={t("Umidade do ar")}
              data={chartData}
              dataKey="humidity"
              color="#3498db"
              unit="%"
              periodId={period}
            />
            <FarmClimateChart
              title={t("Vento")}
              data={chartData}
              dataKey="windSpeedKmh"
              color="#1abb9c"
              unit=" km/h"
              periodId={period}
            />
          </div>

          <h3 className="farm-climate-log__table-title">{t("Registros")}</h3>
          <div className="farm-climate-log__table-wrap">
            <table className="farm-climate-log__table">
              <thead>
                <tr>
                  <th>{t("Horário")}</th>
                  <th>{t("Temperatura")}</th>
                  <th>{t("Vento")}</th>
                  <th>{t("Umidade do ar")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.time}>
                    <td>{formatRowTime(row.time)}</td>
                    <td>
                      {row.temperatureC != null ? `${row.temperatureC}°` : "—"}
                    </td>
                    <td>
                      {row.windSpeedKmh != null ? (
                        <span className="farm-climate-log__wind">
                          {row.windSpeedKmh} km/h
                          {row.windCardinal ? ` ${row.windCardinal}` : ""}
                          <WindDirectionArrow row={row} />
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>{row.humidity != null ? `${row.humidity}%` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </section>
  );
}

export default FarmClimateLog;
